import api from '../utils/api';

/**
 * Task Management API Service
 * เชื่อมต่อกับ Backend Task Management APIs
 * Backend API pattern: /api/projects/:projectId/tasks
 */

/**
 * ดึงรายการงานของโปรเจ็ค
 * @param {number} projectId - Project ID
 * @param {Object} params - Query parameters (status, priority, assigned_to, etc.)
 * @returns {Promise} Response data
 */
export const getTasks = async (projectId, params = {}) => {
  try {
    const response = await api.get(`/api/projects/${projectId}/tasks`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

/**
 * ดึงข้อมูลงานตาม ID
 * @param {number} projectId - Project ID
 * @param {number} taskId - Task ID
 * @returns {Promise} Response data
 */
export const getTaskById = async (projectId, taskId) => {
  try {
    const response = await api.get(`/api/projects/${projectId}/tasks/${taskId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching task ${taskId}:`, error);
    throw error;
  }
};

/**
 * สร้างงานใหม่
 * @param {number} projectId - Project ID
 * @param {Object} taskData - ข้อมูลงาน
 * @returns {Promise} Response data
 */
export const createTask = async (projectId, taskData) => {
  try {
    const response = await api.post(`/api/projects/${projectId}/tasks`, taskData);
    return response.data;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

/**
 * อัพเดทงาน
 * @param {number} taskId - Task ID
 * @param {Object} taskData - ข้อมูลงานที่ต้องการแก้ไข
 * @returns {Promise} Response data
 */
export const updateTask = async (taskId, taskData) => {
  try {
    const response = await api.put(`/api/tasks/${taskId}`, taskData);
    return response.data;
  } catch (error) {
    console.error(`Error updating task ${taskId}:`, error);
    throw error;
  }
};

/**
 * ลบงาน
 * @param {number} taskId - Task ID
 * @returns {Promise} Response data
 */
export const deleteTask = async (taskId) => {
  try {
    const response = await api.delete(`/api/tasks/${taskId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting task ${taskId}:`, error);
    throw error;
  }
};

/**
 * อัพเดทสถานะงาน
 * @param {number} taskId - Task ID
 * @param {string} status - สถานะใหม่ (todo, in_progress, testing, done)
 * @returns {Promise} Response data
 */
export const updateTaskStatus = async (taskId, status) => {
  try {
    const response = await api.patch(`/api/tasks/${taskId}/status`, { status });
    return response.data;
  } catch (error) {
    console.error(`Error updating task status ${taskId}:`, error);
    throw error;
  }
};

/**
 * ดึงรายการงานในรูปแบบ Kanban Board
 * @param {number} projectId - Project ID
 * @returns {Promise} Response data
 */
export const getKanbanTasks = async (projectId) => {
  try {
    const response = await api.get(`/api/projects/${projectId}/tasks/kanban`);
    return response.data;
  } catch (error) {
    console.error('Error fetching kanban tasks:', error);
    throw error;
  }
};

/**
 * ดึงความคิดเห็นของงาน
 * @param {number} taskId - Task ID
 * @returns {Promise} Response data
 */
export const getTaskComments = async (taskId) => {
  try {
    const response = await api.get(`/api/tasks/${taskId}/comments`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching task ${taskId} comments:`, error);
    throw error;
  }
};

/**
 * เพิ่มความคิดเห็นในงาน
 * @param {number} taskId - Task ID
 * @param {Object} commentData - ข้อมูลความคิดเห็น { comment_text }
 * @returns {Promise} Response data
 */
export const addTaskComment = async (taskId, commentData) => {
  try {
    const response = await api.post(`/api/tasks/${taskId}/comments`, commentData);
    return response.data;
  } catch (error) {
    console.error(`Error adding comment to task ${taskId}:`, error);
    throw error;
  }
};

/**
 * แก้ไขความคิดเห็น
 * @param {number} taskId - Task ID
 * @param {number} commentId - Comment ID
 * @param {Object} commentData - ข้อมูลความคิดเห็นที่แก้ไข { comment_text }
 * @returns {Promise} Response data
 */
export const updateTaskComment = async (taskId, commentId, commentData) => {
  try {
    const response = await api.put(`/api/tasks/${taskId}/comments/${commentId}`, commentData);
    return response.data;
  } catch (error) {
    console.error(`Error updating comment ${commentId} on task ${taskId}:`, error);
    throw error;
  }
};

/**
 * ลบความคิดเห็น
 * @param {number} taskId - Task ID
 * @param {number} commentId - Comment ID
 * @returns {Promise} Response data
 */
export const deleteTaskComment = async (taskId, commentId) => {
  try {
    const response = await api.delete(`/api/tasks/${taskId}/comments/${commentId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting comment ${commentId} on task ${taskId}:`, error);
    throw error;
  }
};

/**
 * ดึงรายการไฟล์แนบของงาน
 * @param {number} taskId - Task ID
 * @returns {Promise} Response data
 */
export const getTaskAttachments = async (taskId) => {
  try {
    const response = await api.get(`/api/tasks/${taskId}/attachments`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching task ${taskId} attachments:`, error);
    throw error;
  }
};

/**
 * อัพโหลดไฟล์แนบ
 * @param {number} taskId - Task ID
 * @param {FormData} formData - Form data containing file
 * @returns {Promise} Response data
 */
export const uploadTaskAttachment = async (taskId, formData) => {
  try {
    const response = await api.post(`/api/tasks/${taskId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error uploading attachment to task ${taskId}:`, error);
    throw error;
  }
};

/**
 * ลบไฟล์แนบ
 * @param {number} taskId - Task ID
 * @param {number} attachmentId - Attachment ID
 * @returns {Promise} Response data
 */
export const deleteTaskAttachment = async (taskId, attachmentId) => {
  try {
    const response = await api.delete(`/api/tasks/${taskId}/attachments/${attachmentId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting attachment ${attachmentId} from task ${taskId}:`, error);
    throw error;
  }
};

/**
 * ดาวน์โหลดไฟล์แนบ
 * @param {number} taskId - Task ID
 * @param {number} attachmentId - Attachment ID
 * @returns {Promise} Response with file blob
 */
export const downloadTaskAttachment = async (taskId, attachmentId) => {
  try {
    const response = await api.get(`/api/tasks/${taskId}/attachments/${attachmentId}/download`, {
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    console.error(`Error downloading attachment ${attachmentId} from task ${taskId}:`, error);
    throw error;
  }
};

export default {
  getTasks,
  getTaskById,
  createTask,
  updateTask,
  deleteTask,
  updateTaskStatus,
  getKanbanTasks,
  getTaskComments,
  addTaskComment,
  updateTaskComment,
  deleteTaskComment,
  getTaskAttachments,
  uploadTaskAttachment,
  deleteTaskAttachment,
  downloadTaskAttachment
};
